import React, { useState, useEffect } from 'react';
import { adminFormsService } from '../services/admin-forms.service';
import { Question } from '../types';
import { QuestionDrawer } from './QuestionDrawer';
import { DeleteQuestionConfirmationDialog } from './DeleteQuestionConfirmationDialog';
import { COLORS } from '../../../../core/constants/colors';
import { getFieldTypeLabel } from '../utils/fieldTypeMapper';

type SortOption = 'newest' | 'oldest' | 'a-z' | 'z-a' | 'weight-high' | 'weight-low';

interface QuestionListProps {
  searchQuery?: string;
  onViewQuestion?: (question: Question) => void;
  onEditQuestion?: (question: Question) => void;
  onCreateQuestion?: () => void;
}

export const QuestionList: React.FC<QuestionListProps> = ({
  searchQuery: externalSearchQuery = '',
  onViewQuestion,
  onEditQuestion,
  onCreateQuestion,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit' | 'create'>('view');
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteDialogQuestion, setDeleteDialogQuestion] = useState<Question | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterFieldType, setFilterFieldType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const itemsPerPage = 10;

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      const data = await adminFormsService.getAllQuestions();
      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (question: Question) => {
    setSelectedQuestion(question);
    setDrawerMode('view');
    setShowDrawer(true);
    if (onViewQuestion) onViewQuestion(question);
  };

  const handleEdit = (question: Question, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedQuestion(question);
    setDrawerMode('edit');
    setShowDrawer(true);
    if (onEditQuestion) onEditQuestion(question);
  };

  const handleDelete = async (question: Question, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeleteDialogQuestion(question);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialogQuestion) return;
    setIsDeleting(true);
    try {
      const success = await adminFormsService.deleteQuestion(deleteDialogQuestion.id);
      if (success) {
        await loadQuestions();
        setShowDeleteDialog(false);
        setDeleteDialogQuestion(null);
      } else {
        alert('Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setDeleteDialogQuestion(null);
  };

  const handleSuccess = () => {
    loadQuestions();
    setSelectedQuestion(null);
  };

  const filteredQuestions = questions
    .filter((q) => {
      const matchesSearch =
        q.label.toLowerCase().includes(externalSearchQuery.toLowerCase()) ||
        q.field_id.toLowerCase().includes(externalSearchQuery.toLowerCase()) ||
        (q.description || '').toLowerCase().includes(externalSearchQuery.toLowerCase()) ||
        q.category.toLowerCase().includes(externalSearchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || q.category === filterCategory;
      const matchesFieldType = filterFieldType === 'all' || q.field_type === filterFieldType;
      return matchesSearch && matchesCategory && matchesFieldType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'a-z':
          return a.label.localeCompare(b.label);
        case 'z-a':
          return b.label.localeCompare(a.label);
        case 'weight-high':
          return b.scoring_weight - a.scoring_weight;
        case 'weight-low':
          return a.scoring_weight - b.scoring_weight;
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [externalSearchQuery, filterCategory, filterFieldType, sortBy]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent mb-2"
          style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
        />
        <p className="text-sm" style={{ color: '#6B7280' }}>Loading questions...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filters & Sort */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
        >
          <option value="all">All Categories</option>
          <option value="basic_info">Basic Info</option>
          <option value="financial">Financial</option>
          <option value="operational">Operational</option>
          <option value="strategic">Strategic</option>
          <option value="compliance">Compliance</option>
          <option value="other">Other</option>
        </select>

        <select
          value={filterFieldType}
          onChange={(e) => setFilterFieldType(e.target.value)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
        >
          <option value="all">All Types</option>
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="single-choice">Single Choice</option>
          <option value="multi-choice">Multi Choice</option>
          <option value="url">URL</option>
          <option value="date">Date</option>
          <option value="boolean">Boolean</option>
          <option value="email">Email</option>
          <option value="phone">Phone</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="a-z">A — Z</option>
          <option value="z-a">Z — A</option>
          <option value="weight-high">Weight: High to Low</option>
          <option value="weight-low">Weight: Low to High</option>
        </select>

        {(filterCategory !== 'all' || filterFieldType !== 'all') && (
          <button
            onClick={() => {
              setFilterCategory('all');
              setFilterFieldType('all');
            }}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100"
            style={{ color: '#6B7280' }}
          >
            Clear Filters
          </button>
        )}

        <span className="text-sm ml-auto" style={{ color: '#6B7280' }}>
          {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-3">
        {paginatedQuestions.length > 0 ? (
          paginatedQuestions.map((question) => (
          <div
            key={question.id}
            className="bg-gray-50 rounded-lg p-4 flex items-start justify-between hover:shadow-md transition-all cursor-pointer"
            onClick={() => handleView(question)}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#E0E0E0' }}>
                  {getFieldTypeLabel(question.field_type)}
                </span>
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#E0E0E0' }}>
                  {question.category}
                </span>
                {!question.is_active && (
                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>
                    Inactive
                  </span>
                )}
              </div>
              <p className="font-medium text-sm" style={{ color: '#1F2937' }}>
                {question.label}
              </p>
              {question.description && (
                <p className="text-sm text-gray-600 mt-1">{question.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => handleView(question)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
              >
                View
              </button>
              <button
                onClick={(e) => handleEdit(question, e)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
              >
                Edit
              </button>
              <button
                onClick={(e) => handleDelete(question, e)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:shadow-sm"
                style={{ backgroundColor: '#DC2626' }}
              >
                Delete
              </button>
            </div>
          </div>
          ))
        ) : (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm" style={{ color: '#9CA3AF' }}>
              {externalSearchQuery ? 'No questions found matching your search' : 'No questions yet'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-sm" style={{ color: '#6B7280' }}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredQuestions.length)} of {filteredQuestions.length} questions
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm font-medium" style={{ color: '#6B7280' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Question Drawer */}
      <QuestionDrawer
        mode={drawerMode}
        question={selectedQuestion}
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedQuestion(null);
        }}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteQuestionConfirmationDialog
        isOpen={showDeleteDialog}
        questionLabel={deleteDialogQuestion?.label || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </div>
  );
};

